import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import Swal from "sweetalert2";
import { setSessionExpired, setSessionStartedAt } from "state/slices/sessionSlice";
import { SESSION_DURATION_MS, SESSION_WARNING_BEFORE_EXPIRY_MS } from "utils/constants";
import { getActiveDirectoryAuthToken } from "utils/getActiveDirectoryAuthToken";
import { persistSessionStartedAt } from "utils/sessionTiming";
import { redirectToLogout } from "utils/redirectToLogout";

import "./sweetAlertCustom.css";

// Session lifecycle is driven by ONE clock — sessionStartedAt (see
// sessionSlice.js) — not mouse/keyboard activity. This replaces the old
// 30-minute idle-activity timer, which reset on every mousemove/click/scroll
// and had no relation to how long the user had actually been signed in or to
// the token's real validity: an actively-clicking user could stay signed in
// indefinitely regardless of token age, while a briefly-idle-but-still-valid
// session could get force-logged-out for no real reason.
const EXPIRY_MESSAGES = {
  "permissions-changed": "Your access has changed. Please sign in again to continue.",
  default: "Your login session has expired. Please login again.",
};

const InactiveLogout = ({ children }) => {
  const dispatch = useDispatch();
  const { accounts, instance } = useMsal();
  const sessionExpired = useSelector((state) => state.session.sessionExpired);
  const sessionExpiredReason = useSelector((state) => state.session.sessionExpiredReason);
  const sessionStartedAt = useSelector((state) => state.session.sessionStartedAt);
  const expiredHandledRef = useRef(false);
  const expiredReasonRef = useRef(null);

  useEffect(() => {
    if (!sessionStartedAt) return;

    const elapsed = Date.now() - sessionStartedAt;
    const msUntilWarning = SESSION_DURATION_MS - SESSION_WARNING_BEFORE_EXPIRY_MS - elapsed;
    const msUntilExpiry = SESSION_DURATION_MS - elapsed;

    if (msUntilExpiry <= 0) {
      dispatch(setSessionExpired(true));
      return;
    }

    const warningTimer = setTimeout(() => {
      showExpiryWarning();
    }, Math.max(msUntilWarning, 0));

    const expiryTimer = setTimeout(() => {
      dispatch(setSessionExpired(true));
    }, msUntilExpiry);

    return () => {
      clearTimeout(warningTimer);
      clearTimeout(expiryTimer);
    };
  }, [sessionStartedAt, dispatch]);

  useEffect(() => {
    if (sessionExpired && !expiredHandledRef.current) {
      expiredHandledRef.current = true;
      expiredReasonRef.current = sessionExpiredReason;
      handleExpiredSession();
    }
  }, [sessionExpired]);

  const showExpiryWarning = () => {
    if (accounts.length === 0) return;

    let countdownInterval;
    const countdownEl = () => document.getElementById("session-expiry-countdown");

    // Recomputed from sessionStartedAt on every tick (rather than counting
    // down a local variable) so the number stays accurate even if the tab
    // was backgrounded/throttled and missed a tick or two.
    const renderRemaining = () => {
      const remainingMs = Math.max(SESSION_DURATION_MS - (Date.now() - sessionStartedAt), 0);
      const totalSeconds = Math.ceil(remainingMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = String(totalSeconds % 60).padStart(2, "0");
      const el = countdownEl();
      if (el) el.textContent = `${minutes}:${seconds}`;
    };

    Swal.fire({
      title: "Your session is expiring soon",
      html: `You'll be signed out in <b id="session-expiry-countdown" style="color:#d32f2f">5:00</b>. Stay signed in to keep working.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#006161",
      confirmButtonText: "Stay signed in",
      cancelButtonText: "Log out",
      allowOutsideClick: false,
      didOpen: () => {
        renderRemaining();
        countdownInterval = setInterval(renderRemaining, 1000);
      },
      willClose: () => {
        clearInterval(countdownInterval);
      },
    }).then((result) => {
      if (result.isConfirmed) {
        extendSession();
      } else {
        dispatch(setSessionExpired(true));
      }
    });
  };

  // "Stay signed in": get a genuinely NEW token from Azure AD now (forceRefresh —
  // a plain silent call would just return the cached ID token, which is minutes
  // from expiring, so the backend would reject it and sign the user out soon after
  // the clock reset) and reset the session clock to a full new window. Every
  // subsequent request then picks up the new token via getActiveDirectoryAuthToken.
  const extendSession = async () => {
    const token = await getActiveDirectoryAuthToken({ forceRefresh: true });
    if (!token) {
      // Silent renewal itself failed — getActiveDirectoryAuthToken already
      // dispatched setSessionExpired in that case, so there's nothing more
      // to do here.
      return;
    }
    const newSessionStartedAt = Date.now();
    persistSessionStartedAt(newSessionStartedAt);
    dispatch(setSessionStartedAt(newSessionStartedAt));
  };

  const handleExpiredSession = () => {
    if (accounts.length === 0) return;

    // If the warning modal is still open when the hard ceiling hits, calling
    // Swal.fire() again immediately replaces it — no need to (and don't)
    // call Swal.close() first: closing and re-firing in the same tick races
    // SweetAlert2's own close animation and can cause the new modal to be
    // silently dropped, leaving the user stuck looking at the old one for
    // several seconds while the orphaned auto-logout timer below still
    // eventually fires in the background.
    const doLogout = () => {
      redirectToLogout(instance);
    };

    // If the user clicks OK before the 10s grace period elapses, logout
    // already starts right away — cancel the background timer so it doesn't
    // also fire a second, redundant logoutRedirect call afterwards.
    const autoLogoutTimer = setTimeout(doLogout, 10000);
    const message = EXPIRY_MESSAGES[expiredReasonRef.current] || EXPIRY_MESSAGES.default;

    Swal.fire({
      title: "Session Expired",
      text: message,
      icon: "warning",
      confirmButtonColor: "#006161",
      confirmButtonText: "OK",
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        clearTimeout(autoLogoutTimer);
        doLogout();
      }
    });
  };

  return children;
};

export default InactiveLogout;
